import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ChevronRight, 
  ArrowLeft, 
  Home, 
  Car, 
  Search, 
  PlusCircle, 
  MessageCircle, 
  User, 
  Building, 
  LandPlot, 
  Hotel,
  Zap,
  Truck,
  Ship,
  Bike,
  Image as ImageIcon,
  Clock,
  X,
  SlidersHorizontal,
  Eye,
  Heart
} from 'lucide-react';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLayer, setCurrentLayer] = useState([]);
  const [listings, setListings] = useState([]);
  const [latestListings, setLatestListings] = useState([]);
  const [viewMode, setViewMode] = useState('vitrin'); // Default: 'vitrin' (Vitrin)
  const [history, setHistory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryCount, setSelectedCategoryCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  useEffect(() => {
    // Kullanıcı bilgisini çek
    axios.get('api/get_user_info.php')
      .then(res => {
        if (res.data.logged_in) setUser(res.data.user);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Son eklenen ilanları çek
    fetchLatestListings();
  }, []);

  // Okunmamış mesaj kontrolü
  useEffect(() => {
    if (!user) return;

    const checkUnreadMessages = () => {
      axios.get('api/get_unread_messages_count.php')
        .then(res => {
          if (res.data.unread_count !== undefined) {
            setHasUnreadMessages(res.data.unread_count > 0);
          }
        })
        .catch(err => console.error("Unread messages check error:", err));
    };

    // İlk kontrol
    checkUnreadMessages();

    // Her 5 saniyede bir kontrol et
    const interval = setInterval(checkUnreadMessages, 5000);

    return () => clearInterval(interval);
  }, [user]);

  const fetchLatestListings = () => {
    axios.get('api/get_listings.php?limit=10')
      .then(res => setLatestListings(res.data))
      .catch(err => console.error("Latest listings error:", err));
  };

  const fetchCategories = (parentId) => {
    axios.get(`api/get_categories.php?parent_id=${parentId || ''}`)
      .then(res => {
        if (res.data.length > 0) {
          setCurrentLayer(res.data);
          // Sadece kullanıcı filtreleme butonuna tıkladığında viewMode'u değiştir
          if (viewMode !== 'listings' && viewMode !== 'search') {
            setViewMode('categories');
          }
        } else if (parentId) {
          // Eğer alt kategori yoksa ilanları getir
          fetchListings(parentId);
        }
      })
      .catch(err => console.error("Categories fetch error:", err));
  };

  const fetchListings = (catId) => {
    setLoading(true);
    axios.get(`api/get_listings.php?category_id=${catId}`)
      .then(res => {
        setListings(res.data);
        setViewMode('listings');
        setLoading(false);
      })
      .catch(err => {
        console.error("Listings fetch error:", err);
        setLoading(false);
      });
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    axios.get(`api/search_listings.php?q=${encodeURIComponent(searchQuery)}`)
      .then(res => {
        setListings(res.data);
        setViewMode('search');
        setLoading(false);
      })
      .catch(err => {
        console.error("Search error:", err);
        setLoading(false);
      });
  };

  const handleCategoryClick = (cat) => {
    setHistory([...history, { mode: viewMode, data: currentLayer, cat: selectedCategory, count: selectedCategoryCount }]);
    setSelectedCategory(cat);
    setSelectedCategoryCount(cat.count ?? 0);
    fetchCategories(cat.id);
  };

  const handleBack = () => {
    const lastState = history[history.length - 1];
    if (lastState) {
      setViewMode(lastState.mode);
      setCurrentLayer(lastState.data);
      setSelectedCategory(lastState.cat);
      setSelectedCategoryCount(lastState.count ?? 0);
      setHistory(history.slice(0, -1));
    } else if (viewMode === 'vitrin') {
      setViewMode('categories');
    } else {
      setViewMode('categories');
      fetchCategories(null);
      setSelectedCategory(null);
      setSelectedCategoryCount(0);
    }
  };

  // Filtreleme yolundaki kategoriler (hem kategori hem ilan listesi görünümünde kullanılır)
  const pathCategories = [...(history || []).map(h => h.cat).filter(Boolean), selectedCategory].filter(Boolean);

  const getIcon = (name) => {
    const n = name.toLowerCase();
    if (n === 'emlak') return <Home size={22} />;
    if (n === 'vasıta') return <Car size={22} />;
    if (n.includes('konut')) return <Building size={22} />;
    if (n.includes('arsa')) return <LandPlot size={22} />;
    if (n.includes('turistik')) return <Hotel size={22} />;
    if (n.includes('elektrikli')) return <Zap size={22} />;
    if (n.includes('ticari')) return <Truck size={22} />;
    if (n.includes('deniz')) return <Ship size={22} />;
    if (n.includes('motosiklet')) return <Bike size={22} />;
    return <ChevronRight size={18} />;
  };

  if (loading) return (
    <div className="loading">
      <Zap size={40} className="animate-pulse" />
      <p>Yükleniyor...</p>
    </div>
  );

  return (
    <div className="app-container">
      <header className="mobile-header">
        {(history.length > 0 || (viewMode !== 'categories' && viewMode !== 'vitrin')) ? (
          <button className="back-btn" onClick={handleBack}>
            <ArrowLeft size={20} />
          </button>
        ) : <div style={{width: 36}}></div>}
        <h1>{viewMode === 'listings' ? selectedCategory?.name : (viewMode === 'search' ? 'Arama Sonuçları' : (viewMode === 'vitrin' ? 'VITRIN' : 'KATEGORİLER'))}</h1>
        <div className="user-menu">
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {user.role === 'admin' && (
                <a href="admin.php" title="Admin Paneli" style={{ color: '#007bff', textDecoration: 'none', marginRight: '5px' }}>
                  <SlidersHorizontal size={18} />
                </a>
              )}
              <a href="panel.php" style={{ fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', color: 'inherit' }}>
                {user.username}
                {user.role === 'admin' && (
                  <span title="Onaylı Hesap" style={{ color: '#007bff', display: 'inline-flex' }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" stroke="white" strokeWidth="1" />
                    </svg>
                  </span>
                )}
              </a>
            </div>
          ) : <a href="login.php" style={{fontSize: '14px', textDecoration: 'none', color: '#d32f2f', fontWeight: 'bold'}}>Giriş</a>}
        </div>
      </header>

      {/* Arama Çubuğu */}
      <div className="search-bar-container">
        <form onSubmit={handleSearch} className="search-form">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            placeholder="Kelime, ilan no ile ara..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && <X size={18} className="clear-icon" onClick={() => setSearchQuery('')} />}
        </form>
      </div>

      <main className="category-view">
        {viewMode === 'vitrin' ? (
          <div className="showcase-section" style={{marginTop: '10px'}}>
            {/* Şirket Hesapları Butonu */}
            <div style={{padding: '0 12px 15px', marginBottom: '15px'}}>
              <button 
                onClick={() => window.location.href = 'company_list.php'}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(211, 47, 47, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 47, 47, 0.3)';
                }}
              >
                <Building size={20} />
                <span>Şirket Hesapları</span>
              </button>
            </div>
            
            <div className="section-header">
              <Clock size={18} />
              <span>Son Eklenen İlanlar</span>
            </div>
            <div className="horizontal-scroll">
              {latestListings.map(l => (
                    <div key={l.id} className="scroll-item" onClick={() => window.location.href = `listing_detail.php?id=${l.id}`}>
                      <div className="scroll-image" style={{position: 'relative'}}>
                        {l.video_path ? (
                          <video
                            src={l.video_path}
                            muted
                            playsInline
                            autoPlay
                            loop={false}
                            onTimeUpdate={(e) => {
                              const v = e.target;
                              if (v.currentTime >= 5) v.currentTime = 0;
                            }}
                            className="scroll-video"
                          />
                        ) : l.main_image ? (
                          <img src={l.main_image} alt={l.title} />
                        ) : (
                          <ImageIcon size={24} color="#ccc" />
                        )}
                        {l.is_featured === "1" && (
                          <div style={{position: 'absolute', top: 5, right: 5, background: '#ff9800', color: 'white', fontSize: '8px', padding: '2px 4px', borderRadius: '4px', fontWeight: 'bold'}}>ÖNE ÇIKAN</div>
                        )}
                      </div>
                      <div className="scroll-title">{l.title}</div>
                      <div className="scroll-price">{l.price ? `${new Intl.NumberFormat('tr-TR').format(l.price)} ${l.currency}` : 'Fiyat Belirtilmedi'}</div>
                      <div className="scroll-stats">
                        <span><Eye size={14} /> {l.views ?? 0}</span>
                        <span><Heart size={14} /> {l.favorite_count ?? 0}</span>
                      </div>
                    </div>
              ))}
            </div>
            {latestListings.length === 0 && <p style={{textAlign: 'center', color: '#999', marginTop: '20px'}}>Henüz ilan bulunmuyor.</p>}
          </div>
        ) : viewMode === 'categories' ? (
          <>
            {/* Filtreleme esnasında her alt kategoriye geçilince: yol + Tüm X ilanları */}
            {pathCategories.length > 0 && (
              <div className="listings-header-block filter-bar-sticky">
                <div className="filter-breadcrumb" style={{
                  background: '#f5f5f5',
                  borderBottom: '1px solid #e8e8e8',
                  padding: '12px 15px',
                  fontSize: '14px',
                  color: '#444'
                }}>
                  <span style={{ marginRight: '6px' }}>🏠</span>
                  <span>İlanlar</span>
                  {pathCategories.map((cat, i) => (
                    <span key={cat?.id || i}>
                      <span style={{ margin: '0 6px' }}>&gt;</span>
                      <span>{cat?.name}</span>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => selectedCategory && fetchListings(selectedCategory.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 15px',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Tüm &apos;{pathCategories[pathCategories.length - 1]?.name}&apos; ilanları ({selectedCategoryCount})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </button>
              </div>
            )}
            <div className="category-list">
            {currentLayer.map(cat => (
              <div 
                key={cat.id} 
                className="category-item"
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="cat-info">
                  <div className="cat-icon-bg">
                    {getIcon(cat.name)}
                  </div>
                  <span className="cat-name">{cat.name}</span>
                </div>
                <div className="cat-meta">
                  <span className="cat-count">{cat.count}</span>
                  <ChevronRight size={18} color="#ccc" />
                </div>
              </div>
            ))}
          </div>
          </>
        ) : (
          <>
            {/* İlan listesi görünümünde de aynı yol + Tüm X ilanları (sayı ile) */}
            {viewMode === 'listings' && pathCategories.length > 0 && (
              <div className="listings-header-block filter-bar-sticky">
                <div className="filter-breadcrumb" style={{
                  background: '#f5f5f5',
                  borderBottom: '1px solid #e8e8e8',
                  padding: '12px 15px',
                  fontSize: '14px',
                  color: '#444'
                }}>
                  <span style={{ marginRight: '6px' }}>🏠</span>
                  <span>İlanlar</span>
                  {pathCategories.map((cat, i) => (
                    <span key={cat?.id || i}>
                      <span style={{ margin: '0 6px' }}>&gt;</span>
                      <span>{cat?.name}</span>
                    </span>
                  ))}
                </div>
                <a
                  href="#listings-grid"
                  className="tum-ilanlar-link"
                  style={{
                    display: 'block',
                    padding: '12px 15px',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none'
                  }}
                >
                  Tüm &apos;{selectedCategory?.name}&apos; ilanları ({listings.length})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </a>
              </div>
            )}
            {viewMode === 'search' && (
              <div style={{ marginBottom: '16px', padding: '12px 15px' }}>
                <a
                  href="#listings-grid"
                  style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1a237e',
                    textDecoration: 'none'
                  }}
                >
                  Tüm arama sonuçları ({listings.length})
                  <span style={{ marginLeft: '6px', fontSize: '18px' }}>&gt;</span>
                </a>
              </div>
            )}
            <div id="listings-grid" className="listings-grid">
            {listings.length > 0 ? listings.map(l => (
              <div key={l.id} className="listing-card-modern" onClick={() => window.location.href = `listing_detail.php?id=${l.id}`}>
                <div className="listing-image-container">
                  {l.main_image ? (
                    <img src={l.main_image} alt={l.title} />
                  ) : (
                    <div className="no-image"><ImageIcon size={48} color="#ccc" /></div>
                  )}
                  {l.is_featured === "1" && (
                    <div className="featured-badge">Öne Çıkan ⭐</div>
                  )}
                  <div className="listing-price-tag">
                    {l.price ? `${new Intl.NumberFormat('tr-TR').format(l.price)} ${l.currency}` : 'Fiyat Belirtilmedi'}
                  </div>
                </div>
                <div className="listing-content">
                  <h3 className="listing-title">{l.title}</h3>
                  <div className="listing-stats">
                    <span><Eye size={14} /> {l.views ?? 0}</span>
                    <span><Heart size={14} /> {l.favorite_count ?? 0}</span>
                  </div>
                  <div className="listing-bottom">
                    <span className="listing-location" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {l.username}
                      {l.user_role === 'admin' && (
                        <span style={{ color: '#007bff' }}>
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" stroke="white" strokeWidth="1" />
                          </svg>
                        </span>
                      )}
                    </span>
                    <span className="listing-date">{new Date(l.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{textAlign: 'center', padding: '50px 20px', color: '#999'}}>
                <ImageIcon size={64} style={{marginBottom: '15px', opacity: 0.5}} />
                <p>{viewMode === 'search' ? 'Aradığınız kriterlere uygun ilan bulunamadı.' : 'Bu kategoride henüz onaylanmış ilan bulunmuyor.'}</p>
              </div>
            )}
          </div>
          </>
        )}
      </main>

      <nav className="bottom-nav">
        <button className={`nav-item ${viewMode === 'vitrin' ? 'active' : ''}`} onClick={() => {
          setViewMode('vitrin');
          setHistory([]);
        }}>
          <Home size={22} />
          <span>Vitrin</span>
        </button>
        <button className={`nav-item ${viewMode === 'categories' || viewMode === 'listings' ? 'active' : ''}`} onClick={() => {
          setViewMode('categories');
          fetchCategories(null);
          setHistory([]);
          setSelectedCategory(null);
          setSelectedCategoryCount(0);
        }}>
          <SlidersHorizontal size={22} />
          <span>Filtrele</span>
        </button>
        <button className="nav-item special" onClick={() => window.location.href = 'add_listing.php'}>
          <div className="nav-icon-bg">
            <PlusCircle size={24} />
          </div>
          <span>İlan Ver</span>
        </button>
        <button className="nav-item" onClick={() => window.location.href = 'messages.php'} style={{ position: 'relative' }}>
          <MessageCircle size={22} />
          <span>Mesajlar</span>
          {hasUnreadMessages && <span className="message-dot"></span>}
        </button>
        <button className="nav-item" onClick={() => window.location.href = 'panel.php'}>
          <User size={22} />
          <span>Hesabım</span>
        </button>
      </nav>
    </div>
  );
}

export default App;
